import { useState } from "react";
import type {
  CmmdProjectAgentMember,
  CreateCmmdProjectAgentMemberParams,
} from "@cmmd-forge/contracts";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import {
  displayNameForCreate,
  isProviderLockedAgentKind,
  providerLockedDisplayName,
} from "./agentMemberDisplayName";

type CreateAgentInput = Omit<CreateCmmdProjectAgentMemberParams, "idempotencyKey">;

export function CreateAgentMemberDialog(props: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onCreate: (input: CreateAgentInput) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [kind, setKind] = useState<CmmdProjectAgentMember["agentKind"]>("forge");
  const [role, setRole] = useState<CmmdProjectAgentMember["primaryRole"]>("swe");
  const [capabilities, setCapabilities] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lockedName = isProviderLockedAgentKind(kind);
  const submit = async () => {
    const displayName = displayNameForCreate(kind, name);
    if (!displayName) return;
    setSaving(true);
    setError(null);
    try {
      await props.onCreate({
        displayName,
        agentKind: kind,
        primaryRole: role,
        capabilities: capabilities
          .split(",")
          .map((capability) => capability.trim())
          .filter(Boolean),
        policy: {},
      });
      setName("");
      setCapabilities("");
      props.onOpenChange(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Agent Member could not be created.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogPopup>
        <DialogPanel>
          <DialogHeader>
            <DialogTitle>Add Agent Member</DialogTitle>
            <DialogDescription>
              Create a reusable project-scoped bot identity. Codex, Cursor, and Claude use the
              provider name. Only Forge can be named.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <AgentMemberSelect
              label="Agent type"
              value={kind}
              options={["forge", "codex", "claude", "cursor", "integration", "custom"]}
              onChange={(value) => setKind(value as typeof kind)}
            />
            {lockedName ? (
              <p className="text-sm">
                <span className="font-medium">Name</span>
                <span className="mt-1 block text-muted-foreground">
                  {providerLockedDisplayName(kind)} · locked to the provider
                </span>
              </p>
            ) : (
              <label className="block text-sm font-medium">
                Name
                <Input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  className="mt-1"
                  autoFocus
                />
              </label>
            )}
            <AgentMemberSelect
              label="Primary role"
              value={role}
              options={["project_lead", "swe", "pm", "designer", "architect", "reviewer", "qa"]}
              onChange={(value) => setRole(value as typeof role)}
            />
            <label className="block text-sm font-medium">
              Capabilities
              <Input
                value={capabilities}
                onChange={(event) => setCapabilities(event.target.value)}
                placeholder="frontend, testing, review"
                className="mt-1"
              />
              <span className="mt-1 block text-xs font-normal text-muted-foreground">
                Optional, comma-separated specialties.
              </span>
            </label>
            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => props.onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              disabled={saving || (!lockedName && !name.trim())}
              onClick={() => void submit()}
            >
              {saving ? "Creating…" : "Create agent"}
            </Button>
          </DialogFooter>
        </DialogPanel>
      </DialogPopup>
    </Dialog>
  );
}

function AgentMemberSelect(props: {
  readonly label: string;
  readonly value: string;
  readonly options: readonly string[];
  readonly onChange: (value: string) => void;
}) {
  return (
    <label className="block text-sm font-medium">
      {props.label}
      <select
        className="mt-1 h-9 w-full rounded-md border bg-background px-2 text-sm"
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
      >
        {props.options.map((value) => (
          <option key={value} value={value}>
            {roleLabel(value)}
          </option>
        ))}
      </select>
    </label>
  );
}

function roleLabel(value: string): string {
  if (value === "swe") return "Software engineer";
  if (value === "pm") return "Product manager";
  if (value === "qa") return "Quality assurance";
  return value.replaceAll("_", " ");
}
